import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Paper,
  Chip,
  Box,
  TextField,
  CircularProgress,
  Alert,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import apiClient from '../../../config/axios';
import ModalFinanceCourseMonthDetails from './ModalFinanceCourseMonthDetails';

dayjs.locale('it');

const FinanceBalanceCourses = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [balanceData, setBalanceData] = useState([]);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const [activeCatalogCourseCount, setActiveCatalogCourseCount] = useState(0);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await apiClient.get('/api/courses?status=published');
        setCourses(response.data || []);
      } catch (err) {
        console.error('Error fetching courses:', err);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    if (!selectedCourseId) {
      setBalanceData([]);
      setTeacherInfo(null);
      setActiveCatalogCourseCount(0);
      return;
    }

    const fetchCourseBalance = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(`/api/finance/course-balance/${selectedCourseId}`);
        setBalanceData(response.data.monthlyBalance || []);
        setTeacherInfo(response.data.teacher);
        setActiveCatalogCourseCount(
          typeof response.data.activeCatalogCourseCount === 'number'
            ? response.data.activeCatalogCourseCount
            : 0
        );
      } catch (err) {
        console.error('Error fetching course balance:', err);
        setError('Errore nel caricamento del bilancio del corso');
        setBalanceData([]);
        setTeacherInfo(null);
        setActiveCatalogCourseCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchCourseBalance();
  }, [selectedCourseId]);

  const selectedCourseLabel = useMemo(() => {
    const c = courses.find((x) => String(x._id) === String(selectedCourseId));
    if (!c) return '';
    const venuePart = c.venue ? ` (${c.venue})` : '';
    return `${c.name}${venuePart}`;
  }, [courses, selectedCourseId]);

  const formatAmount = (amount) => {
    return `${amount > 0 ? '+' : ''}${amount.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€`;
  };

  const getNetColor = (net) => {
    if (net > 0) return 'success';
    if (net < 0) return 'error';
    return 'default';
  };

  const columns = [
    {
      field: 'month',
      headerName: 'Mese',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='medium'>
          {params.value}
        </Typography>
      ),
      sortComparator: (v1, v2) => {
        const date1 = dayjs(v1, 'MMMM YY', 'it');
        const date2 = dayjs(v2, 'MMMM YY', 'it');
        return date1.diff(date2);
      },
    },
    {
      field: 'income',
      headerName: 'Entrate',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant='body2' color='success.main'>
          {formatAmount(params.value)}
        </Typography>
      ),
    },
    {
      field: 'expenses',
      headerName: 'Spese',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant='body2' color='error.main'>
          {params.value > 0 ? formatAmount(-params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'teacherCompensation',
      headerName: 'Rimborsi Docente',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant='body2' color='error.main'>
          {params.value > 0 ? formatAmount(-params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'otherCompensation',
      headerName: 'Altri Rimborsi',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant='body2' color='error.main'>
          {params.value > 0 ? formatAmount(-params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'net',
      headerName: 'Bilancio',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Chip
          label={formatAmount(params.value)}
          color={getNetColor(params.value)}
          size='small'
          variant={params.value === 0 ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      field: 'details',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Tooltip title='Dettaglio entrate e uscite'>
          <IconButton
            size='small'
            aria-label='Dettaglio entrate e uscite'
            onClick={(e) => {
              e.stopPropagation();
              setDetailsRow(params.row);
              setDetailsOpen(true);
            }}
          >
            <VisibilityOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 2, mb: 2, maxWidth: 'md' }}>
      <Typography variant='h6' gutterBottom>
        Bilancio per Corso
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        <Box component='span' sx={{ color: 'success.main', fontWeight: 600 }}>Entrate</Box>: iscrizioni e rinnovi corso. <Box component='span' sx={{ color: 'error.main', fontWeight: 600 }}>Uscite</Box>: spese per corso, rimborsi docente e altri rimborsi docente.
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
      altri rimborsi docente è ripartito tra i corsi attivi nel catalogo.
      </Typography> 
      <Box sx={{ mb: 2 }}>
        <TextField
          select
          label="Seleziona un corso"
          value={selectedCourseId}
          onChange={(e) => setSelectedCourseId(e.target.value)}
          size="small"
          sx={{ mt:2,minWidth: 300 }}
          disabled={coursesLoading}
        >
          <MenuItem value="">
            <em>Nessuno</em>
          </MenuItem>
          {[...courses].sort((a, b) => a.name.localeCompare(b.name, 'it')).map((course) => (
            <MenuItem key={course._id} value={course._id}>
              {course.name}{course.venue ? ` (${course.venue})` : ''}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={30} />
        </Box>
      )}

      {!loading && selectedCourseId && teacherInfo && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Docente: {teacherInfo.name} {teacherInfo.surname} — Compenso per lezione: {teacherInfo.salaryPerLesson}€
        </Typography>
      )}

      {!loading && selectedCourseId && balanceData.length === 0 && !error && (
        <Typography variant='body2' color='text.secondary'>
          Nessun dato disponibile per questo corso
        </Typography>
      )}

      {!loading && balanceData.length > 0 && (
        <DataGrid
          rows={balanceData}
          columns={columns}
          hideFooter
          disableRowSelectionOnClick
          getRowId={(row) => row.month}
          initialState={{
            sorting: {
              sortModel: [{ field: 'month', sort: 'desc' }],
            },
          }}
          sx={{
            backgroundColor: 'transparent',
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
              backgroundColor: '#fff',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#fff',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#fff',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      )}

      <ModalFinanceCourseMonthDetails
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setDetailsRow(null);
        }}
        monthLabel={detailsRow?.month}
        courseLabel={selectedCourseLabel}
        row={detailsRow}
        activeCatalogCourseCount={activeCatalogCourseCount}
      />
    </Paper>
  );
};

export default FinanceBalanceCourses;
